import { getTranslatedName } from '../../../utils/functions/translation.functions'
import { Journey } from '../product/journey'
import { Translation } from '../translation/translation'
import { Trip, TripLight } from './trip'

export type TripLabelInput = {
  hasTransport?: boolean
  departure?: Pick<NonNullable<TripLight['departure']>, 'translations'>
  arrival?: Pick<NonNullable<TripLight['arrival']>, 'translations'>
  journeyPlace?: Pick<NonNullable<TripLight['journeyPlace']>, 'name'>
  transportMeans?: Pick<NonNullable<TripLight['transportMeans']>, 'translations'>
}

type GetTripPriceInput = {
  departure?: Pick<NonNullable<Trip['departure']>, 'translations'>
  arrival?: Pick<NonNullable<Trip['arrival']>, 'translations'>
  prices?: (Pick<Trip['prices'][number], 'price'> & {
    periods: Pick<Trip['prices'][number]['periods'][number], 'id'>[]
  })[]
}

export class TripProperties {
  static getTripLightLabel(trip?: TripLabelInput): string {
    if (!trip) return ''
    if (!trip.transportMeans && trip.journeyPlace) return trip.journeyPlace.name
    if (!trip.departure || !trip.arrival) return ''
    const departure = getTranslatedName(trip.departure)
    const arrival = getTranslatedName(trip.arrival)
    if (departure === arrival) return departure
    const result = [departure, arrival]
    return result.join(' - ')
  }

  static getTripLabel(trip?: TripLabelInput): string {
    if (!trip) return ''
    const result = [
      this.getTripLightLabel(trip),
      this.handleTransportMeansName(trip.transportMeans)
    ]
    return result.join(' - ')
  }

  static getDepartureOrJourneyPlaceLabel(trip?: TripLabelInput): string {
    if (!trip) return ''
    if (trip.hasTransport === false) return trip.journeyPlace?.name ?? ''
    return trip.departure ? getTranslatedName(trip.departure) : (trip.journeyPlace?.name ?? '')
  }

  static getTripPrice(journey: Journey, trip?: GetTripPriceInput): string {
    if (!trip || !journey.date.period?.id) return ''
    const price = trip.prices?.find(
      (price) =>
        journey.date.period &&
        price.periods.map((period) => period.id).includes(journey.date.period.id)
    )
    if (!trip.departure || !trip.arrival) return ''
    const result = [
      getTranslatedName(trip.departure),
      getTranslatedName(trip.arrival),
      `${price?.price ?? 0} €`
    ]
    return result.join(' - ')
  }

  static getTripPriceValue(journey: Journey, trip?: Trip): number | undefined {
    if (!trip || !journey.date.period?.id) return 0
    return (
      trip.prices.find(
        (price) =>
          journey.date.period &&
          price.periods.map((period) => period.id).includes(journey.date.period.id)
      )?.price ?? 0
    )
  }

  static handleTransportMeansName(transportMeans?: { translations: Translation[] }): string {
    if (!transportMeans) return 'Sans Transport'
    return getTranslatedName(transportMeans)
  }

  // Same rule as the backend PDF helpers: the flight transport means is stored with code / name
  // "Avion" in production, "FLIGHT" in the seed data
  static isFlight(transportMeans?: { translations: Translation[]; code?: string }): boolean {
    if (!transportMeans) return false
    const keys = [transportMeans.code, getTranslatedName(transportMeans)]
      .filter((key): key is string => !!key)
      .map((key) => key.trim().toLowerCase())
    return keys.some((key) => ['avion', 'flight'].includes(key))
  }
}
